/**
 * Domain Management Hooks
 * React Query hooks for managing domains in the EdgeMail Admin Console
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EdgeMailAdminAPI } from '../services/EdgeMailAdminAPI';
import type {
  Domain,
  DomainDnsRecords,
  DnsVerificationResult,
  AddDomainRequest,
} from '../types/admin.types';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const domainQueryKeys = {
  all: ['domains'] as const,
  lists: () => [...domainQueryKeys.all, 'list'] as const,
  details: () => [...domainQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...domainQueryKeys.details(), id] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Fetch list of domains for the current tenant
 */
export function useDomainList() {
  return useQuery({
    queryKey: domainQueryKeys.lists(),
    queryFn: () => EdgeMailAdminAPI.getDomains(),
  });
}

/**
 * Fetch a single domain with DNS records
 */
export function useDomain(domainId: string | null) {
  return useQuery({
    queryKey: domainQueryKeys.detail(domainId || ''),
    queryFn: () => EdgeMailAdminAPI.getDomain(domainId!),
    enabled: !!domainId,
  });
}

/**
 * Add a new domain
 */
export function useAddDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: AddDomainRequest }) =>
      EdgeMailAdminAPI.addDomain(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainQueryKeys.lists() });
    },
  });
}

/**
 * Verify domain DNS records
 */
export function useVerifyDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domainId: string) => EdgeMailAdminAPI.verifyDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainQueryKeys.all });
    },
  });
}

/**
 * Set domain as primary
 */
export function useSetPrimaryDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domainId: string) => EdgeMailAdminAPI.setPrimaryDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainQueryKeys.lists() });
    },
  });
}

/**
 * Delete a domain
 */
export function useDeleteDomain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domainId: string) => EdgeMailAdminAPI.deleteDomain(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainQueryKeys.lists() });
    },
  });
}

/**
 * Regenerate DKIM keys
 */
export function useRegenerateDkim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domainId: string) => EdgeMailAdminAPI.regenerateDkim(domainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainQueryKeys.all });
    },
  });
}
